import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL, API } from "../../../config/api";
import axiosService from "../../../services/axiosServicesVisit";
import axios from "axios";
import { toast } from 'react-toastify';

// Fonction helper pour nettoyer et formater les filtres
const createCleanFilters = (filters) => {
  const cleanFilters = {};
  for (const key in filters) {
    const value = filters[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      cleanFilters[key] = Array.isArray(value) ? value.join(',') : String(value);
    }
  }
  return cleanFilters;
};

// Thunk pour la liste paginée principale des échantillons donnés
export const getGivenSamplesSummary = createAsyncThunk(
  'givenSamples/getSummary',
  async ({ page = 1, limit = 10, filters = {}, signal }, thunkAPI) => {
    try {
      const cleanFilters = createCleanFilters(filters);
      const queryParams = { page: String(page), limit: String(limit), ...cleanFilters };
      const params = new URLSearchParams(queryParams);
      
      if (!API?.GIVEN_SAMPLES?.GET_SUMMARY) {
        return thunkAPI.rejectWithValue('API path for given samples summary is not configured');
      }
      
      const endpointPath = API.GIVEN_SAMPLES.GET_SUMMARY;
      const fullApiUrl = `${API_URL}${endpointPath}?${params.toString()}`;
      
      const response = await axiosService.get(fullApiUrl, { signal });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue({ message: 'Request canceled', cancelled: true });
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement du résumé.';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Thunk pour récupérer TOUS les KPIs en un seul appel
export const getGivenSamplesAllKpis = createAsyncThunk(
  'givenSamples/getAllKpis',
  async ({ filters = {}, signal }, thunkAPI) => {
    try {
      const cleanFilters = createCleanFilters(filters);
      const params = new URLSearchParams(cleanFilters);

      if (!API?.GIVEN_SAMPLES?.GET_ALL_KPIS) { 
        return thunkAPI.rejectWithValue('API path for all kpis is not configured');
      }
      
      const endpointPath = API.GIVEN_SAMPLES.GET_ALL_KPIS; 
      const fullApiUrl = `${API_URL}${endpointPath}?${params.toString()}`;
      
      const response = await axiosService.get(fullApiUrl, { signal });
      return response.data; 
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue({ message: 'Request canceled', cancelled: true });
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement des KPIs.';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Thunk pour le tableau croisé par délégué
export const getDelegateSamplePivotData = createAsyncThunk(
  'givenSamples/fetchDelegatePivot',
  async ({ delegateId, filters = {}, delegateInfo = {}, signal }, thunkAPI) => {
    try {
      const cleanFilters = createCleanFilters(filters);
      const params = new URLSearchParams(cleanFilters);
      
      if (!API?.GIVEN_SAMPLES?.GET_DELEGATE_PIVOT_BASE) {
        return thunkAPI.rejectWithValue('API base path for delegate pivot is not configured');
      }
      
      const basePath = API.GIVEN_SAMPLES.GET_DELEGATE_PIVOT_BASE;
      const endpointPath = `${basePath}/${delegateId}`;
      const fullApiUrl = `${API_URL}${endpointPath}?${params.toString()}`;
      
      const response = await axiosService.get(fullApiUrl, { signal });
      return { pivotData: response.data, delegateInfoRequest: delegateInfo }; 
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue({ message: 'Request canceled', cancelled: true, delegateInfoRequest: delegateInfo });
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement du tableau croisé.';
      return thunkAPI.rejectWithValue({ error: errorMessage, delegateInfoRequest: delegateInfo });
    }
  }
);

// --- THUNK POUR L'EXPORT DU RÉSUMÉ ---
export const exportGivenSamplesSummary = createAsyncThunk(
  'givenSamples/exportSummary',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const cleanFilters = createCleanFilters(filters);
      const params = new URLSearchParams(cleanFilters);
      const url = `${API_URL}${API.GIVEN_SAMPLES.EXPORT_SUMMARY}?${params.toString()}`;

      const response = await axiosService.get(url, { responseType: 'blob' });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = `resume_echantillons_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      return { success: true };
    } catch (error) {
      toast.error("Erreur lors de l'export du résumé.");
      return rejectWithValue("Erreur d'export");
    }
  }
);

// --- THUNK POUR L'EXPORT DU TABLEAU CROISÉ ---
export const exportDelegatePivot = createAsyncThunk(
  'givenSamples/exportDelegatePivot',
  async ({ delegateId, filters = {}, delegateName }, { rejectWithValue }) => {
    try {
      const cleanFilters = createCleanFilters(filters);
      const params = new URLSearchParams(cleanFilters);

      // --- LA CORRECTION FINALE EST ICI ---
      // 1. On utilise la constante que vous avez corrigée.
      const basePath = API.GIVEN_SAMPLES.EXPORT_DELEGATE_PIVOT_BASE;
      
      // 2. On construit le chemin relatif complet.
      const url = `${basePath}/${delegateId}/export/csv?${params.toString()}`;

      // 3. On passe ce chemin relatif à axiosService.
      const response = await axiosService.get(url, { responseType: 'blob' });
      
      // ... (le reste du code pour télécharger le fichier est correct)
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const safeName = (delegateName || `delegate_${delegateId}`).replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `pivot_${safeName}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      return { success: true };
    } catch (error) {
      toast.error("Erreur lors de l'export du tableau croisé.");
      return rejectWithValue("Erreur d'export");
    }
  }
);